/*
    Play codes
*/
export const usePlayCodes = async (body) => {
    const { error: e, data } = await $fetch('/api/codes/play', {
        method: 'post',
        body,
    });

    if (e) {
        useSentryError(e, {
            useThrow: true,
            payload: body,
            response: data,
        });
    }

    const wincodeCookie = useCookie('wincode');
    wincodeCookie.value = null;

    setCurrentWins(data);
    const localePath = useLocalePath();
    await navigateTo(localePath('/current-wins'));
};

/*
    Check codes
*/
export const useCheckCodes = async (body) => {
    const { error: e, data } = await $fetch('/api/codes/check', {
        method: 'post',
        body,
    });

    if (e) {
        useSentryError(e, {
            useThrow: true,
            payload: body,
            response: data,
        });
    }

    return data;
};

/*
    Free participation (Gratisteilnahme)
*/
export const useFreeParticipation = async (body) => {
    const { error: e, data } = await useDirectApiFetch('/api/receipts/free-participation', {
        method: 'post',
        body,
    });

    if (e) {
        useSentryError(e, {
            useThrow: true,
            payload: body,
            response: data,
        });
    }

    return data;
};

/*
    Show free codes
*/
export const useShowFreeCodes = async () => {
    const { error: e, data } = await useFetch('/api/codes/free');

    if (e.value) {
        useSentryError(e.value, {
            useThrow: true,
            payload: body,
            response: data,
        });
    }

    return data.value;
};

/*
    Get played codes
*/
export const useGetPlayedCodes = async (body) => {
    const { error: e, data } = await useFetch('/api/codes/played', {
        method: 'post',
        body,
    });

    if (e.value) {
        useSentryError(e.value, {
            useThrow: true,
            payload: body,
            response: data,
        });
    }
    return data.value;
};

/*
    Get Multicode
*/
export const useGetMultiCode = async (codeOrigin) => {
    if (!codeOrigin) return null;

    const { error: e, data } = await $fetch('/api/codes/get-multi-code', {
        method: 'get',
        params: {
            codeOrigin,
        },
    });

    if (data.code) {
        const winCodeCookie = useCookie('wincode');
        winCodeCookie.value = data.code;
        const localePath = useLocalePath();
        await navigateTo(localePath('/play-codes'));
    }

    if (e) {
        useSentryError(e, {
            useThrow: true,
            response: data,
        });
    }

    return data;
};

/*
    Redeem win
*/
export const useRedeemWin = async (body) => {
    try {
        const data = await useDirectApiFetch('/api/prizes/redeem', {
            method: 'post',
            body,
        });

        return data;
    } catch (e) {
        useSentryError(e, {
            useThrow: true,
            payload: body,
            response: e?.data,
        });
    }

    return null;
};

/*
    Get quizzes
*/
export const useGetQuizzes = async () => {
    const { error: e, data } = await useFetch('/api/quizzes/get');

    if (e.value) {
        useSentryError(e.value, {
            useThrow: true,
            response: data,
        });
    }

    return data.value;
};

/*
    Play quiz
*/
export const usePlayQuiz = async (body) => {
    const { error: e, data } = await useFetch('/api/quizzes/play', {
        method: 'post',
        body: {
            ...body,
        },
    });

    if (e.value) {
        useSentryError(e.value, {
            useThrow: true,
            payload: body,
            response: data,
        });
    }

    return data.value;
};

/*
    Get quiz
*/
export const useGetQuiz = async (body) => {
    const { error: e, data } = await useFetch('/api/quizzes/find', {
        method: 'post',
        body: {
            uuid: body,
        },
    });

    if (e.value) {
        useSentryError(e.value, {
            useThrow: true,
            payload: body,
            response: data,
        });
    }

    return data.value;
};

/*
    Play Photo upload
*/
export const usePlayPhotoUpload = async (body) => {
    const { files } = body;

    const formData = new FormData();
    files.forEach((file) => {
        formData.append('files', file); // Append each file to the FormData
    });

    const { error: e, data } = await useFetch('/api/photo-uploads/upload', {
        method: 'post',
        body: formData,
    });

    if (e.value) {
        useSentryError(e.value, {
            useThrow: true,
            payload: body,
            response: data,
        });
    }

    return data.value;
};

/*
    Get photo uploads
*/
export const useGetPhotoUploads = async (params) => {
    const { error: e, data } = await $fetch('/api/photo-uploads/get', {
        method: 'get',
        params: {
            pageSize: params.pageSize,
            page: params.page,
            userId: params.onlyMe ? user.value.id : null,
        },
    });

    if (e) {
        useSentryError(e, {
            useThrow: true,
            payload: body,
            response: data,
        });
    }

    return data;
};

/*
    Play receipt
*/
const pollReceiptStatus = async (receiptDocumentId, { intervalMs = 5000 } = {}) => {
    while (true) {
        const status = await useDirectApiFetch(
            `/api/receipts/status/${receiptDocumentId}`,
            { method: 'GET' }
        );
        if (status.done) {
            if (status.status === 'duplicate' || status.status === 'unreadable') {
                const msg = status.status === 'duplicate' ? 'duplicate' : 'not_readable';
                const err = new Error(msg);
                err.data = { error: { message: msg } };
                throw err;
            }
            return { is_win: status.is_win, result: status.result, receipt_document_id: receiptDocumentId };
        }
        await new Promise((r) => setTimeout(r, intervalMs));
    }
};

export const usePlayReceipt = async ({ files }) => {
    const file = files?.[0];

    if (!file) throw new Error('No file selected');

    const formData = new FormData();
    formData.append('file', file);

    const { receipt_document_id } = await useDirectApiFetch('/api/receipts/play', {
        method: 'POST',
        body: formData,
    });

    return await pollReceiptStatus(receipt_document_id);
};